import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Megaphone,
  Plus,
  Calendar,
  Building2,
  Users,
  Upload,
  FileText,
  Download,
  Loader2,
  CheckCircle,
  Search,
  X,
  ChevronRight,
  Link as LinkIcon,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function copyShareLink(announcementId) {
  const origin = window.location.origin;
  const url = `${origin}/opportunities/${announcementId}`;
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url)
      .then(() => toast.success('Shareable link copied to clipboard'))
      .catch(() => toast.error('Could not copy. Link: ' + url));
  } else {
    // Fallback
    const ta = document.createElement('textarea');
    ta.value = url;
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); toast.success('Link copied'); }
    catch { toast.error('Could not copy. Link: ' + url); }
    document.body.removeChild(ta);
  }
}

export default function AnnouncementsAdmin({ token }) {
  const [view, setView] = useState('list'); // 'list' | 'create' | 'detail'
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAnn, setSelectedAnn] = useState(null);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (e) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (view === 'create') {
    return (
      <CreateAnnouncementForm
        token={token}
        onDone={() => {
          setView('list');
          fetchAnnouncements();
        }}
        onCancel={() => setView('list')}
      />
    );
  }

  if (view === 'detail' && selectedAnn) {
    return (
      <AnnouncementDetail
        token={token}
        announcement={selectedAnn}
        onBack={() => {
          setSelectedAnn(null);
          setView('list');
          fetchAnnouncements();
        }}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white">R&amp;D Opportunities</h3>
          <p className="text-sm text-zinc-500">Call-for-proposal announcements sent to matched experts</p>
        </div>
        <Button
          onClick={() => setView('create')}
          className="gap-2"
          data-testid="new-announcement-btn"
        >
          <Plus className="w-4 h-4" />
          New Announcement
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <p>No announcements yet. Create your first call for proposals.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map((a) => (
            <Card
              key={a.id}
              className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors"
              data-testid={`ann-card-${a.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => { setSelectedAnn(a); setView('detail'); }}
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="font-semibold text-white">{a.title}</h4>
                      {a.is_active ? (
                        <Badge className="bg-green-500/10 text-green-400 hover:bg-green-500/20">Active</Badge>
                      ) : (
                        <Badge className="bg-zinc-700 text-zinc-400">Expired</Badge>
                      )}
                    </div>
                    <p className="text-sm text-zinc-400 line-clamp-2 mb-2">{a.description}</p>
                    <div className="flex flex-wrap gap-4 text-xs text-zinc-500">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {a.sponsor_institution}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Deadline: {a.deadline}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {(a.notified_expert_ids || []).length} notified
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {a.proposal_count || 0} proposals
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-2 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      onClick={(e) => { e.stopPropagation(); copyShareLink(a.id); }}
                      data-testid={`copy-link-${a.id}`}
                    >
                      <Copy className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Copy Link</span>
                    </Button>
                    <ChevronRight
                      className="w-5 h-5 text-zinc-600 cursor-pointer"
                      onClick={() => { setSelectedAnn(a); setView('detail'); }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateAnnouncementForm({ token, onDone, onCancel }) {
  const [step, setStep] = useState(1); // 1=details, 2=preview, 3=upload
  const [form, setForm] = useState({
    title: '',
    description: '',
    sponsor_institution: '',
    deadline: '',
    topic: '',
    min_match_score: 20
  });
  const [matches, setMatches] = useState([]);
  const [selectedExpertIds, setSelectedExpertIds] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePreview = async () => {
    if (!form.topic.trim()) { toast.error('Enter a topic'); return; }
    setPreviewing(true);
    try {
      const res = await fetch(`${API}/admin/announcements/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topic: form.topic, min_match_score: Number(form.min_match_score) })
      });
      if (res.ok) {
        const data = await res.json();
        setMatches(data.matches || []);
        setSelectedExpertIds((data.matches || []).map((m) => m.id));
        setStep(2);
      } else {
        toast.error('Preview failed');
      }
    } catch (e) {
      toast.error('Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleCreate = async () => {
    if (selectedExpertIds.length === 0) { toast.error('Select at least one expert'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/admin/announcements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: form.title,
          description: form.description,
          sponsor_institution: form.sponsor_institution,
          deadline: form.deadline,
          topic: form.topic,
          min_match_score: Number(form.min_match_score),
          expert_ids: selectedExpertIds
        })
      });
      if (res.ok) {
        const data = await res.json();
        setCreatedId(data.id);
        setStep(3);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch (e) {
      toast.error('Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpload = async () => {
    if (!pdfFile) { toast.error('Choose a PDF'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pdfFile);
      const res = await fetch(`${API}/admin/announcements/${createdId}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (res.ok) {
        toast.success('Announcement published & experts notified!');
        onDone();
      } else {
        toast.error('Upload failed');
      }
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const canProceedStep1 = form.title && form.description && form.sponsor_institution && form.deadline && form.topic;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white">New Announcement</h3>
          <p className="text-sm text-zinc-500">Step {step} of 3</p>
        </div>
        <Button variant="ghost" onClick={onCancel} className="text-zinc-400">
          <X className="w-4 h-4 mr-2" /> Cancel
        </Button>
      </div>

      {step === 1 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
                placeholder="e.g., DST Call for Quantum Materials Research"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="ann-title"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Description *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                placeholder="Brief summary of the call for proposals…"
                rows={4}
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="ann-desc"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Sponsor Institution *</Label>
                <Input
                  value={form.sponsor_institution}
                  onChange={(e) => update('sponsor_institution', e.target.value)}
                  placeholder="e.g., DST, India"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="ann-sponsor"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Deadline *</Label>
                <Input
                  type="date"
                  value={form.deadline}
                  onChange={(e) => update('deadline', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="ann-deadline"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Topic / Keywords (used to match experts) *</Label>
              <Input
                value={form.topic}
                onChange={(e) => update('topic', e.target.value)}
                placeholder="e.g., quantum materials, condensed matter physics"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="ann-topic"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Minimum Match Score: {form.min_match_score}%</Label>
              <input
                type="range"
                min="10"
                max="80"
                step="5"
                value={form.min_match_score}
                onChange={(e) => update('min_match_score', e.target.value)}
                className="w-full"
                data-testid="ann-min-score"
              />
              <p className="text-xs text-zinc-500">Experts scoring at or above this will be suggested.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                onClick={handlePreview}
                disabled={!canProceedStep1 || previewing}
                className="gap-2"
                data-testid="preview-matches-btn"
              >
                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Preview Matched Experts
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-6 space-y-4">
            <div>
              <h4 className="text-white font-semibold mb-1">
                {matches.length} expert{matches.length !== 1 ? 's' : ''} matched
              </h4>
              <p className="text-sm text-zinc-500">
                Uncheck anyone you don&apos;t want to notify. {selectedExpertIds.length} selected.
              </p>
            </div>
            {matches.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                No experts matched. Try lowering the min score or refining the topic.
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto space-y-2">
                {matches.map((m) => {
                  const checked = selectedExpertIds.includes(m.id);
                  return (
                    <div
                      key={m.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        checked ? 'bg-zinc-800 border-primary/50' : 'bg-zinc-900 border-zinc-800 hover:bg-zinc-800/50'
                      }`}
                      onClick={() => {
                        setSelectedExpertIds((ids) =>
                          checked ? ids.filter((x) => x !== m.id) : [...ids, m.id]
                        );
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {}}
                        className="w-4 h-4"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium">{m.name}</p>
                        <p className="text-xs text-zinc-500 truncate">{m.employment || '—'}</p>
                      </div>
                      <Badge className="bg-primary/10 text-primary">{m.match_score}%</Badge>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex justify-between gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(1)} className="border-zinc-700 text-zinc-300">
                Back
              </Button>
              <Button
                onClick={handleCreate}
                disabled={submitting || selectedExpertIds.length === 0}
                className="gap-2"
                data-testid="create-announcement-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Continue → Upload PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-green-400 mb-2">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm">Announcement created. Upload the PDF to send notifications.</span>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Call-for-Proposal PDF *</Label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-zinc-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary file:text-white hover:file:bg-primary/90"
                data-testid="ann-pdf-input"
              />
              {pdfFile && (
                <p className="text-xs text-zinc-500">
                  {pdfFile.name} — {(pdfFile.size / (1024 * 1024)).toFixed(1)} MB
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                onClick={handleUpload}
                disabled={uploading || !pdfFile}
                className="gap-2"
                data-testid="upload-pdf-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Upload &amp; Notify Experts
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AnnouncementDetail({ token, announcement, onBack }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/admin/announcements/${announcement.id}/proposals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setProposals(data.proposals || []);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [token, announcement.id]);

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await fetch(`${API}/admin/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { toast.error('Download failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  return (
    <div>
      <Button variant="ghost" onClick={onBack} className="mb-4 text-zinc-400">
        ← Back to Announcements
      </Button>

      <Card className="bg-zinc-900 border-zinc-800 mb-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <h3 className="text-xl font-semibold text-white">{announcement.title}</h3>
            {announcement.is_active ? (
              <Badge className="bg-green-500/10 text-green-400">Active</Badge>
            ) : (
              <Badge className="bg-zinc-700 text-zinc-400">Expired</Badge>
            )}
          </div>
          <p className="text-zinc-400 mb-4 whitespace-pre-wrap">{announcement.description}</p>
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-zinc-500 text-xs">Sponsor</p>
              <p className="text-white">{announcement.sponsor_institution}</p>
            </div>
            <div>
              <p className="text-zinc-500 text-xs">Deadline</p>
              <p className="text-white">{announcement.deadline}</p>
            </div>
            <div>
              <p className="text-zinc-500 text-xs">Notified Experts</p>
              <p className="text-white">{(announcement.notified_expert_ids || []).length}</p>
            </div>
          </div>
          {announcement.pdf_file_id && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadFile(announcement.pdf_file_id, announcement.pdf_filename || 'announcement.pdf')}
              className="gap-2 mt-4 border-zinc-700 text-zinc-300"
            >
              <Download className="w-4 h-4" />
              Download Call PDF
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => copyShareLink(announcement.id)}
            className="gap-2 mt-4 ml-2 border-zinc-700 text-zinc-300"
            data-testid="copy-share-link-detail"
          >
            <LinkIcon className="w-4 h-4" />
            Copy Shareable Link
          </Button>
        </CardContent>
      </Card>

      <h4 className="text-lg font-semibold text-white mb-3">
        Submitted Proposals ({proposals.length})
      </h4>
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : proposals.length === 0 ? (
        <div className="text-center py-12 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-lg">
          No proposals yet.
        </div>
      ) : (
        <div className="space-y-2">
          {proposals.map((p) => (
            <Card key={p.id} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">{p.expert_name}</p>
                  <p className="text-xs text-zinc-500 truncate">{p.expert_email}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Submitted {new Date(p.submitted_at).toLocaleString()} — {p.pdf_filename}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadFile(p.pdf_file_id, p.pdf_filename)}
                  className="gap-2 border-zinc-700 text-zinc-300"
                  data-testid={`download-proposal-${p.id}`}
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
